import { useState } from "react";
import { Link } from "react-router-dom";
import type { CardDetail, FieldDiff } from "../../api/models";
import { VerdictBadge } from "../../components/Badge";
import { PriceLine } from "../../components/PriceLine";
import { productUrl } from "../../lib/catalog";
import { formatDate, showValue } from "../../lib/format";
import { productionCardType, truthIdOf } from "./candidates";

export function Diffs({ diffs }: { diffs: FieldDiff[] }) {
  if (diffs.length === 0) return null;
  return (
    <ul className="diffs">
      {diffs.map((d) => (
        <li key={d.field}>
          {d.field}: <span className="want">{showValue(d.want)}</span> → <span className="got">{showValue(d.got)}</span>
        </li>
      ))}
    </ul>
  );
}

export function RunsPanel({ card }: { card: CardDetail }) {
  const [open, setOpen] = useState<number | null>(null);
  if (card.runs.length === 0) return <p className="muted">No runs yet.</p>;
  return (
    <div className="table-wrap">
      <table className="compact">
        <thead>
          <tr>
            <th>Run</th>
            <th>Date</th>
            <th className="num">Listings</th>
            <th>Pricing</th>
            <th>Pick</th>
            <th>Verdict</th>
            <th>Diffs</th>
            <th>Error</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {card.runs.map((r) => {
            const truthId = truthIdOf(card, r.scored.basis);
            const expanded = open === r.id;
            const runType = r.cardInfo?.card_type;
            const cardType = typeof runType === "string" && runType ? runType : productionCardType(card);
            return [
              <tr key={r.id}>
                <td>
                  <Link to={`/runs/${r.runId}`}>#{r.runId}</Link>
                </td>
                <td className="nowrap">{formatDate(r.ranAt)}</td>
                <td className="num">
                  {r.ebayListings}
                  {r.ebayDegraded && <span className="error"> *</span>}
                </td>
                <td>{r.pricing}</td>
                <td>
                  {r.pickedName ?? <span className="muted">—</span>}
                  {r.pickedId && <span className="mono muted"> #{r.pickedId}</span>}
                </td>
                <td>
                  <VerdictBadge verdict={r.scored.verdict} />
                  {r.scored.sportWrong && <span className="badge badge-wrong_pick"> sport</span>}
                </td>
                <td>
                  <Diffs diffs={r.scored.diffs} />
                </td>
                <td className="error">{r.error ? `${r.error.stage}: ${r.error.message}` : ""}</td>
                <td>
                  <button type="button" className="link" onClick={() => setOpen(expanded ? null : r.id)}>
                    {expanded ? "hide" : `${r.products.length} candidates`}
                  </button>
                </td>
              </tr>,
              expanded ? (
                <tr key={`${r.id}-products`}>
                  <td colSpan={9}>
                    {r.products.length === 0 && <span className="muted">No products returned.</span>}
                    <table className="compact">
                      <tbody>
                        {r.products.map((p) => {
                          const isPick = p.id === r.pickedId;
                          const isTruth = p.id === truthId;
                          return (
                            <tr key={p.id} className={isTruth ? "selected" : undefined}>
                              <td>
                                <a href={productUrl(p.id, cardType)} target="_blank" rel="noreferrer" className="mono">
                                  {p.id}
                                </a>
                              </td>
                              <td style={{ fontWeight: isPick ? 700 : undefined }}>{p.name}</td>
                              <td className="muted">{p.console}</td>
                              <td>
                                <PriceLine prices={p.prices} />
                              </td>
                              <td>
                                {isTruth && <span className="badge badge-truth">truth</span>}{" "}
                                {isPick && <span className="badge">pick</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </td>
                </tr>
              ) : null,
            ];
          })}
        </tbody>
      </table>
    </div>
  );
}
